"use client";

import { type MotionValue, motion, useTransform } from "motion/react";
import { useChartStable } from "./chart-context";
import {
  type ChartProjection,
  lerp,
  polygonPath,
  ProjectedAxisLabels,
  projectView,
  segmentsPath,
  useChartProjection,
  useProjectionProgress,
} from "./projection";

export interface QuadrantTile {
  key: string;
  label: string;
  color: string;
  tint: number;
  x: [number, number];
  y: [number, number];
  labelX: "start" | "end";
  labelY: "top" | "bottom";
  dimmed?: boolean;
}

export interface QuadrantTilesProps {
  tiles: ReadonlyArray<QuadrantTile>;
  xDividers?: ReadonlyArray<number>;
  yDividers?: ReadonlyArray<number>;
  xGuides?: ReadonlyArray<number>;
  yGuides?: ReadonlyArray<number>;
  xTitle?: string;
  yTitle?: string;
  xEndLabels?: [string, string];
  isoTint?: number;
}

const LABEL_INSET_X = 16;
const LABEL_INSET_TOP = 22;
const LABEL_INSET_BOTTOM = 12;
const DIMMED_OPACITY = 0.3;
const DEFAULT_ISO_TINT = 0.14;
const FLOOR_ISO_TINT = 0.03;
const Y_TITLE_OFFSET = 40;
const X_TITLE_OFFSET = 40;
const X_END_OFFSET = 20;
const Y_END_OFFSET = 12;

type Point = readonly [number, number];
type Segment = readonly [Point, Point];

interface PixelRect {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

const clampTo = (v: number, a: number, b: number): number =>
  Math.min(Math.max(v, Math.min(a, b)), Math.max(a, b));

function useFlatOpacity(projection: ChartProjection | null) {
  const e = useProjectionProgress(projection);
  return useTransform(e, [0, 0.4], [1, 0]);
}

function TileShape({
  projection,
  rect,
  color,
  tint,
  isoTint,
  e,
}: {
  projection: ChartProjection | null;
  rect: PixelRect;
  color: string;
  tint: number;
  isoTint: number;
  e: MotionValue<number>;
}) {
  const d = useTransform(e, (v) => {
    if (!projection) return "";
    const view = projection.at(v);
    return polygonPath([
      projectView(view, rect.x0, rect.y0, 0),
      projectView(view, rect.x1, rect.y0, 0),
      projectView(view, rect.x1, rect.y1, 0),
      projectView(view, rect.x0, rect.y1, 0),
    ]);
  });
  const fillOpacity = useTransform(e, (v) => lerp(tint, isoTint, v));

  if (!projection) {
    return (
      <rect
        fill={color}
        fillOpacity={tint}
        height={Math.abs(rect.y1 - rect.y0)}
        width={Math.abs(rect.x1 - rect.x0)}
        x={Math.min(rect.x0, rect.x1)}
        y={Math.min(rect.y0, rect.y1)}
      />
    );
  }
  return <motion.path d={d} fill={color} style={{ fillOpacity }} />;
}

function TileLabel({
  projection,
  at,
  textAnchor,
  text,
  e,
}: {
  projection: ChartProjection | null;
  at: Point;
  textAnchor: "start" | "end";
  text: string;
  e: MotionValue<number>;
}) {
  const x = useTransform(e, (v) =>
    projection ? projection.project(at[0], at[1], 0, v)[0] : at[0]
  );
  const y = useTransform(e, (v) =>
    projection ? projection.project(at[0], at[1], 0, v)[1] : at[1]
  );
  return (
    <motion.text
      fill="var(--muted-foreground)"
      fontSize={11}
      textAnchor={textAnchor}
      x={x}
      y={y}
    >
      {text}
    </motion.text>
  );
}

function ProjectedLines({
  projection,
  segments,
  stroke,
  strokeDasharray,
  opacity,
  e,
}: {
  projection: ChartProjection | null;
  segments: ReadonlyArray<Segment>;
  stroke: string;
  strokeDasharray: string;
  opacity?: number;
  e: MotionValue<number>;
}) {
  const d = useTransform(e, (v) => {
    if (!projection) return segmentsPath(segments);
    const view = projection.at(v);
    return segmentsPath(
      segments.map(
        ([a, b]) =>
          [
            projectView(view, a[0], a[1], 0),
            projectView(view, b[0], b[1], 0),
          ] as const
      )
    );
  });
  return (
    <motion.path
      d={d}
      fill="none"
      opacity={opacity}
      stroke={stroke}
      strokeDasharray={strokeDasharray}
      strokeWidth={1}
    />
  );
}

export function QuadrantTiles({
  tiles,
  xDividers = [],
  yDividers = [],
  xGuides = [],
  yGuides = [],
  xTitle,
  yTitle,
  xEndLabels,
  isoTint = DEFAULT_ISO_TINT,
}: QuadrantTilesProps) {
  const { xScale: anyXScale, yScale, innerHeight } = useChartStable();
  const projection = useChartProjection();
  const e = useProjectionProgress(projection);
  const flatOpacity = useFlatOpacity(projection);

  const xScale = anyXScale as unknown as {
    (value: number): number | undefined;
    domain(): unknown[];
  };
  const xDomain = xScale.domain().map(Number) as [number, number];
  const yDomain = yScale.domain() as [number, number];
  const px = (v: number) => xScale(clampTo(v, xDomain[0], xDomain[1])) ?? 0;
  const py = (v: number) => yScale(clampTo(v, yDomain[0], yDomain[1])) ?? 0;
  const xLo = Math.min(...xDomain);
  const xHi = Math.max(...xDomain);
  const yLo = Math.min(...yDomain);
  const yHi = Math.max(...yDomain);
  const left = px(xLo);
  const right = px(xHi);
  const top = py(yHi);
  const bottom = py(yLo);

  const strong: Segment[] = [
    ...xDividers
      .filter((v) => v > xLo && v < xHi)
      .map((v): Segment => [
        [px(v), top],
        [px(v), bottom],
      ]),
    ...yDividers
      .filter((v) => v > yLo && v < yHi)
      .map((v): Segment => [
        [left, py(v)],
        [right, py(v)],
      ]),
  ];
  const faint: Segment[] = [
    ...xGuides
      .filter((v) => v > xLo && v < xHi)
      .map((v): Segment => [
        [px(v), top],
        [px(v), bottom],
      ]),
    ...yGuides
      .filter((v) => v > yLo && v < yHi)
      .map((v): Segment => [
        [left, py(v)],
        [right, py(v)],
      ]),
  ];

  const yTitleX = left - Y_TITLE_OFFSET;

  return (
    <g aria-hidden pointerEvents="none">
      {projection ? (
        <TileShape
          color="var(--foreground)"
          e={e}
          isoTint={FLOOR_ISO_TINT}
          projection={projection}
          rect={{ x0: left, x1: right, y0: top, y1: bottom }}
          tint={0}
        />
      ) : null}
      {tiles.map((tile) => {
        const rect: PixelRect = {
          x0: px(tile.x[0]),
          x1: px(tile.x[1]),
          y0: py(tile.y[1]),
          y1: py(tile.y[0]),
        };
        if (rect.x0 === rect.x1 || rect.y0 === rect.y1) {
          return null;
        }
        const onLeft = tile.labelX === "start";
        const onTop = tile.labelY === "top";
        const labelAt: Point = [
          onLeft ? left + LABEL_INSET_X : right - LABEL_INSET_X,
          onTop ? top + LABEL_INSET_TOP : bottom - LABEL_INSET_BOTTOM,
        ];
        return (
          <g
            key={tile.key}
            opacity={tile.dimmed ? DIMMED_OPACITY : 1}
            style={{ transition: "opacity 0.15s ease-in-out" }}
          >
            <TileShape
              color={tile.color}
              e={e}
              isoTint={isoTint}
              projection={projection}
              rect={rect}
              tint={tile.tint}
            />
            <TileLabel
              at={labelAt}
              e={e}
              projection={projection}
              text={tile.label}
              textAnchor={onLeft ? "start" : "end"}
            />
          </g>
        );
      })}
      <g>
        <ProjectedLines
          e={e}
          projection={projection}
          segments={strong}
          stroke="var(--grid-strong)"
          strokeDasharray="3 4"
        />
        <ProjectedLines
          e={e}
          opacity={0.5}
          projection={projection}
          segments={faint}
          stroke="var(--grid)"
          strokeDasharray="2 6"
        />
      </g>
      <motion.g style={{ opacity: flatOpacity }}>
        {xTitle ? (
          <text
            fill="var(--muted-foreground)"
            fontSize={11}
            textAnchor="middle"
            x={(left + right) / 2}
            y={bottom + X_TITLE_OFFSET}
          >
            {xTitle} →
          </text>
        ) : null}
        {yTitle ? (
          <text
            fill="var(--muted-foreground)"
            fontSize={11}
            textAnchor="middle"
            transform={`rotate(-90 ${yTitleX} ${innerHeight / 2})`}
            x={yTitleX}
            y={innerHeight / 2}
          >
            {yTitle} →
          </text>
        ) : null}
        {xEndLabels ? (
          <>
            <text
              fill="var(--subtle)"
              fontSize={10}
              textAnchor="middle"
              x={left}
              y={bottom + X_END_OFFSET}
            >
              {xEndLabels[0]}
            </text>
            <text
              fill="var(--subtle)"
              fontSize={10}
              textAnchor="middle"
              x={right}
              y={bottom + X_END_OFFSET}
            >
              {xEndLabels[1]}
            </text>
          </>
        ) : null}
        <text
          fill="var(--subtle)"
          fontSize={10}
          textAnchor="end"
          x={left - Y_END_OFFSET}
          y={bottom + 4}
        >
          {yLo}
        </text>
        <text
          fill="var(--subtle)"
          fontSize={10}
          textAnchor="end"
          x={left - Y_END_OFFSET}
          y={top + 8}
        >
          {yHi}
        </text>
      </motion.g>
      {projection && xTitle ? (
        <ProjectedAxisLabels
          axis="x"
          budget="xAxisTick"
          dy={22}
          labels={[
            {
              key: "x-title",
              text: `${xTitle} →`,
              x: (left + right) / 2,
              y: innerHeight,
              z: 0,
            },
          ]}
        />
      ) : null}
      {projection && yTitle ? (
        <ProjectedAxisLabels
          axis="y"
          budget="xAxisTick"
          dx={-10}
          labels={[
            {
              key: "y-title",
              text: `${yTitle} →`,
              x: 0,
              y: innerHeight / 2,
              z: 0,
            },
          ]}
        />
      ) : null}
    </g>
  );
}

QuadrantTiles.displayName = "QuadrantTiles";

export default QuadrantTiles;
